import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { AvailabilityBlock, TimeSlot } from '../../models/types';
import { db } from '../config';

// Collection reference
const availabilityRef = collection(db, 'availability');

// In-memory cache for availability data
interface AvailabilityCache {
  [key: string]: {
    data: boolean;
    timestamp: number;
    ttl: number;
  };
}

const availabilityCache: AvailabilityCache = {};
const DEFAULT_CACHE_TTL = 30000; // 30 seconds

// Generate cache key for availability check
const generateCacheKey = (vesselId: string, date: string, timeSlot: TimeSlot): string => {
  return `${vesselId}_${date}_${timeSlot}`;
};

// Check if cache entry is still valid
const isCacheValid = (timestamp: number, ttl: number): boolean => {
  return Date.now() - timestamp < ttl;
};

// Get availability from cache or database
export const getAvailabilityWithCache = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot
): Promise<boolean> => {
  const cacheKey = generateCacheKey(vesselId, date, timeSlot);
  const cached = availabilityCache[cacheKey];
  
  // Return cached result if valid
  if (cached && isCacheValid(cached.timestamp, cached.ttl)) {
    console.log(`Cache hit for availability: ${cacheKey}`);
    return cached.data;
  }
  
  // Fetch from database
  console.log(`Cache miss for availability: ${cacheKey}, fetching from database`);
  const isAvailable = await checkAvailabilityInDatabase(vesselId, date, timeSlot);
  
  // Cache the result
  availabilityCache[cacheKey] = {
    data: isAvailable,
    timestamp: Date.now(),
    ttl: DEFAULT_CACHE_TTL
  };
  
  return isAvailable;
};

// Invalidate cache for specific availability
export const invalidateAvailabilityCache = (vesselId: string, date: string, timeSlot: TimeSlot): void => {
  const cacheKey = generateCacheKey(vesselId, date, timeSlot);
  delete availabilityCache[cacheKey];
  console.log(`Invalidated cache for: ${cacheKey}`);
};

// Invalidate all cache entries for a vessel on a specific date
export const invalidateVesselDateCache = (vesselId: string, date: string): void => {
  const keysToDelete = Object.keys(availabilityCache).filter(key => 
    key.startsWith(`${vesselId}_${date}_`)
  );
  
  keysToDelete.forEach(key => {
    delete availabilityCache[key];
  });
  
  console.log(`Invalidated ${keysToDelete.length} cache entries for vessel ${vesselId} on ${date}`);
};

// Database availability check (separated for caching)
const checkAvailabilityInDatabase = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot
): Promise<boolean> => {
  try {
    // Check for existing reservations that conflict with the time slot
    const reservationsQuery = query(
      collection(db, 'reservations'),
      where('vesselId', '==', vesselId),
      where('date', '==', date),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const reservationsSnapshot = await getDocs(reservationsQuery);
    
    // Check for overlapping time slots
    for (const doc of reservationsSnapshot.docs) {
      const reservation = doc.data();
      if (reservation.timeSlot && timeSlotOverlaps(timeSlot, reservation.timeSlot)) {
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking availability in database:', error);
    return false;
  }
};

// Helper function to check if time slots overlap
const timeSlotOverlaps = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
  const convertTimeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  if (!slot1.includes('-') || !slot2.includes('-')) {
    return false;
  }
  
  const [start1, end1] = slot1.split('-');
  const [start2, end2] = slot2.split('-');
  
  const start1Minutes = convertTimeToMinutes(start1);
  const end1Minutes = convertTimeToMinutes(end1);
  const start2Minutes = convertTimeToMinutes(start2);
  const end2Minutes = convertTimeToMinutes(end2);
  
  return start1Minutes < end2Minutes && end1Minutes > start2Minutes;
};

// Create availability block with cache invalidation
export const createAvailabilityBlockWithCache = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot,
  isAvailable: boolean = true
): Promise<AvailabilityBlock> => {
  try {
    const result = await createAvailabilityBlock(vesselId, date, timeSlot, isAvailable);
    
    // Invalidate related cache entries
    invalidateAvailabilityCache(vesselId, date, timeSlot);
    
    return result;
  } catch (error) {
    console.error('Error creating availability block with cache:', error);
    throw error;
  }
};

// Create a new availability block
export const createAvailabilityBlock = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot,
  isAvailable: boolean = true
): Promise<AvailabilityBlock> => {
  try {
    const timestamp = Date.now();
    const blockId = uuidv4();
    
    const newBlock: AvailabilityBlock = {
      id: blockId,
      vesselId,
      date,
      timeSlot,
      isAvailable,
      createdAt: timestamp,
      updatedAt: timestamp
    };
    
    await setDoc(doc(availabilityRef, blockId), newBlock);
    return newBlock;
  } catch (error) {
    console.error('Error creating availability block:', error);
    throw error;
  }
};

// Get availability block by ID
export const getAvailabilityById = async (blockId: string): Promise<AvailabilityBlock | null> => {
  try {
    const blockDoc = await getDoc(doc(availabilityRef, blockId));
    
    if (blockDoc.exists()) {
      return blockDoc.data() as AvailabilityBlock;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting availability block:', error);
    throw error;
  }
};

// Update availability block
export const updateAvailabilityBlock = async (
  blockId: string,
  blockData: Partial<AvailabilityBlock>
): Promise<void> => {
  try {
    const blockRef = doc(availabilityRef, blockId);
    await updateDoc(blockRef, {
      ...blockData,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating availability block:', error);
    throw error;
  }
};

// Delete availability block
export const deleteAvailabilityBlock = async (blockId: string): Promise<void> => {
  try {
    await deleteDoc(doc(availabilityRef, blockId));
  } catch (error) {
    console.error('Error deleting availability block:', error);
    throw error;
  }
};

// Get availability by vessel and date range
export const getAvailabilityByVesselAndDateRange = async (
  vesselId: string,
  startDate: string,
  endDate: string
): Promise<AvailabilityBlock[]> => {
  try {
    const q = query(
      availabilityRef,
      where('vesselId', '==', vesselId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AvailabilityBlock);
  } catch (error) {
    console.error('Error getting availability by vessel and date range:', error);
    throw error;
  }
};

// Get all vessel availability for a specific date
export const getAvailabilityByDate = async (date: string): Promise<AvailabilityBlock[]> => {
  try {
    const q = query(
      availabilityRef,
      where('date', '==', date),
      where('isAvailable', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AvailabilityBlock);
  } catch (error) {
    console.error('Error getting availability by date:', error);
    throw error;
  }
};

// Get vessel availability for a specific date and time slot
export const getAvailabilityByDateAndTimeSlot = async (
  date: string,
  timeSlot: TimeSlot
): Promise<AvailabilityBlock[]> => {
  try {
    const q = query(
      availabilityRef,
      where('date', '==', date),
      where('timeSlot', '==', timeSlot),
      where('isAvailable', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AvailabilityBlock);
  } catch (error) {
    console.error('Error getting availability by date and time slot:', error);
    throw error;
  }
};

// Create multiple availability blocks for a date range (for bulk setup)
export const createAvailabilityForDateRange = async (
  vesselId: string,
  startDate: string,
  endDate: string,
  timeSlots: TimeSlot[],
  isAvailable: boolean = true
): Promise<AvailabilityBlock[]> => {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const blocks: AvailabilityBlock[] = [];
    
    for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
      const dateStr = day.toISOString().split('T')[0];
      
      for (const timeSlot of timeSlots) {
        const block = await createAvailabilityBlock(
          vesselId,
          dateStr,
          timeSlot,
          isAvailable
        );
        
        blocks.push(block);
      }
    }
    
    return blocks;
  } catch (error) {
    console.error('Error creating availability for date range:', error);
    throw error;
  }
}; 